import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Icon, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery, useTheme, Typography, Card, CardContent, CardActions, Button, Chip } from '@mui/material';
import { Edit, Delete, InfoOutlined } from '@mui/icons-material';

import { ToolsListing } from '../../components/custom';
import { LayoutBase } from '../../layouts';
import { IDamsList, DamsServices } from '../../services/api/dams/DamServices';
import { useAuthContext } from '../../context';
import { CustomAlert } from '../../components/shared/VAlert';
import { ConfirmationDialog } from '../../components/shared/VConfirmationDialog';


export const ListDam: React.FC = () => {

    const navigate = useNavigate();
    const [rows, setRows] = useState<IDamsList[]>([]);
    const { userData } = useAuthContext();
    const [data, setData] = useState<IDamsList[]>();
    const [searchText, setSearchText] = useState('');
    const [damIdToDelete, setDamIdToDelete] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loadin, setIsLoading] = useState<boolean>(false);
    const { id } = useParams();
    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));

    const handleDelete = async (uuid: string) => {
        setDamIdToDelete(uuid);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setDamIdToDelete(null);
    };

    useEffect(() => {
        fetchData();
        setIsLoading(true);
    }, [userData, id]);


    const fetchData = () => {

        setAlertInfo({ open: false, message: '', severity: 'info' });
        if (userData.nivel === 'adm') {
            if (id) {
                DamsServices.getListDam(id)
                    .then((result) => {
                        if (result instanceof Error) {
                            setAlertInfo({ open: true, message: result.message, severity: 'error' });
                        } else {
                            if (result.length === 0) {
                                setAlertInfo({ open: true, message: 'Nenhum cadastro localizado!', severity: 'info' });
                                setIsLoading(false);
                            } else {
                                setData(result);
                                setIsLoading(false);
                            }
                        }
                    });
                setIsLoading(false);
            }
        } else {
            if (id) {
                DamsServices.getUnit(id)
                    .then((result) => {
                        if (result instanceof Error) {
                            setAlertInfo({ open: true, message: result.message, severity: 'error' });
                        } else {
                            if (result.length === 0) {
                                setAlertInfo({ open: true, message: 'Nenhum cadastro localizado!', severity: 'info' });
                                setIsLoading(false);
                            } else {
                                const filteredData = result.filter(barragem => barragem.status === true);
                                setData(filteredData);
                                setIsLoading(false);
                            }
                        }
                    });
                setIsLoading(false);
            }
        }
    };

    const handleConfirmDelete = async () => {
        if (damIdToDelete) {
            const result = await DamsServices.delet(damIdToDelete);
            if (result instanceof Error) {
                setAlertInfo({ open: true, message: 'Não foi possível excluir a barragem! ' + result.message, severity: 'error' });
            } else {
                setAlertInfo({ open: true, message: 'Barragem excluída!', severity: 'success' });
                setTimeout(() => {
                    fetchData();
                }, 1600);
            }
            handleCloseDialog();
        }
    };

    useEffect(() => {
        if (data !== undefined) {
            const filteredData = data.filter(barragem =>
                barragem.nome.toLowerCase().includes(searchText.toLowerCase())
            );
            setRows(filteredData);
        }
    }, [data, searchText]);

    const MobileDamCard = ({ row }: { row: IDamsList }) => (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: '15px' }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" component="div" sx={{ wordBreak: 'break-word', flex: 1, mr: 1 }}>
                        {row.nome}
                    </Typography>
                    {userData.nivel === 'adm' && (
                        <Chip
                            label={row.status === true ? 'Ativo' : 'Desativado'}
                            size="small"
                            color={row.status === true ? 'success' : 'error'}
                            variant="outlined"
                        />
                    )}
                </Box>
            </CardContent>

            <CardActions sx={{ flexDirection: 'column', gap: 1, p: 2, pt: 0 }}>
                <Button
                    startIcon={<InfoOutlined />}
                    onClick={() => navigate(`./detalhes/${row.uuid}`)}
                    size="small"
                    variant="outlined"
                    fullWidth
                >
                    Detalhes
                </Button>

                {(userData.permissoes.includes('edit') || userData.permissoes.includes('remove')) && (
                    <Box display="flex" gap={1} justifyContent="center" width="100%">
                        {userData.permissoes.includes('edit') && (
                            <Button
                                startIcon={<Edit />}
                                onClick={() => navigate(`./detalhes/${row.uuid}`)}
                                size="small"
                                variant="outlined"
                                color="secondary"
                                fullWidth
                            >
                                Editar
                            </Button>
                        )}
                        {userData.permissoes.includes('remove') && (
                            <Button
                                color='error'
                                startIcon={<Delete />}
                                onClick={() => handleDelete(row.uuid)}
                                size="small"
                                variant="outlined"
                                fullWidth
                                disabled={row.status === false}
                            >
                                Excluir
                            </Button>
                        )}
                    </Box>
                )}
            </CardActions>
        </Card>
    );

    return (
        <LayoutBase
            title='Barragens'
            tollBar={
                <ToolsListing
                    textoBotaoNovo='Novo'
                    showNewButton={userData.permissoes.includes('add')}
                    mostrarInputBusca
                    textoDaBusca={searchText}
                    onClickNew={() => navigate('./detalhes/novo')}
                    aoMudarTextoBusca={texto => setSearchText(texto)}
                />
            }
        >
            <Box width={'100%'}>
                {loadin ?
                    <LinearProgress />
                    :
                    smDown ? (
                        <Box px={2} pt={2}>
                            {rows && rows.length > 0 ? (
                                rows.map((row) => (
                                    <MobileDamCard key={row.uuid} row={row} />
                                ))
                            ) : (
                                <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '15px' }}>
                                    <Typography color="text.secondary">
                                        Nenhuma barragem encontrada
                                    </Typography>
                                </Paper>
                            )}
                        </Box>
                    ) : (
                        <Box padding={2} marginLeft={-1}>
                            <TableContainer component={Paper} variant='outlined' sx={{ borderRadius: '25px' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Nome</TableCell>
                                            <TableCell>Ações</TableCell>
                                            {userData.nivel === 'adm' ? <TableCell>Status</TableCell> : null}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map(barragem => (
                                            <TableRow key={barragem.uuid}>
                                                <TableCell>{barragem.nome}</TableCell>
                                                <TableCell align='left' >
                                                    {userData.permissoes.includes('edit') ?
                                                        <IconButton size='small' onClick={() => navigate(`./detalhes/${barragem.uuid}`)}>
                                                            <Icon color='primary'>edit</Icon>
                                                        </IconButton>
                                                        : null
                                                    }
                                                    {userData.permissoes.includes('remove') ?
                                                        <IconButton size='small' onClick={() => handleDelete(barragem.uuid)} disabled={barragem.status === false}>
                                                            <Icon color={barragem.status === false ? 'disabled' : 'error'}>delete</Icon>
                                                        </IconButton>
                                                        : null
                                                    }
                                                </TableCell>
                                                {userData.nivel === 'adm' ? <TableCell>{barragem.status == true ? 'Ativo' : 'Desativado'}</TableCell> : null}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )

                }
                {alertInfo.open &&
                    <CustomAlert
                        message={alertInfo.message}
                        severity={alertInfo.severity}
                    />
                }
                <ConfirmationDialog
                    isOpen={dialogOpen}
                    onClose={handleCloseDialog}
                    onConfirm={handleConfirmDelete}
                    title="Confirmação de Exclusão"
                    message="Tem certeza que deseja excluir esta barragem?"
                />
            </Box>
        </LayoutBase>
    );
};